import { useState } from 'react';
import { supabase } from '../lib/constants';
import { COMPETENCIES, APPROVAL_STATUSES, MILESTONE_LEVELS } from '../lib/constants';
import ActivityLog from './ActivityLog';

const ACTIVITY_LABELS = {
  teaching_lecture: 'Teaching Lecture', case_report: 'Case Report',
  mortality_morbidity: 'Mortality & Morbidity', journal_club: 'Journal Club',
  quality_improvement: 'Quality Improvement Project', research_project: 'Research Project',
  other: 'Other',
};
const ACTIVITY_COLORS = {
  teaching_lecture: '#2D6A8A', case_report: '#3A7D6B', mortality_morbidity: '#8A4D5A',
  journal_club: '#6B4D8A', quality_improvement: '#8A6B3A', research_project: '#4D5A8A', other: '#6B6B6B',
};

export default function CaseLog({ entries, activities, onEdit, onRefresh, onRefreshActivities, isMobile }) {
  const [tab, setTab]           = useState('cases'); // cases | activities
  const [selected, setSelected] = useState(null);
  const [filter, setFilter]     = useState('all');
  const [deleting, setDeleting] = useState(false);

  const filtered = filter === 'all' ? entries : entries.filter(e => e.approval_status === filter);
  const comp = (id) => COMPETENCIES.find(c => c.id === id);

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    setDeleting(true);
    await supabase.from('entries').delete().eq('id', id);
    setSelected(null); onRefresh(); setDeleting(false);
  };

  // Detail view for a case
  if (selected) {
    const e = selected;
    const status = APPROVAL_STATUSES[e.approval_status];
    const level  = MILESTONE_LEVELS.find(l => l.n === e.milestone_level);
    return (
      <div style={{ background: '#F7F4F0', minHeight: '100vh', paddingBottom: isMobile ? 80 : 0 }}>
        <div style={{ background: '#fff', padding: isMobile ? '14px 16px' : '16px 40px', borderBottom: '1px solid #E8E4E0', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
          <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#2D6A8A', fontSize: 14, cursor: 'pointer', padding: 0 }}>← Back</button>
          <span style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 16 : 20, color: '#1A1A1F' }}>Case Detail</span>
        </div>
        <div style={{ padding: isMobile ? 16 : '24px 40px', maxWidth: isMobile ? '100%' : 900, margin: '0 auto' }}>
          {status && <div style={{ padding: '10px 14px', borderRadius: 8, background: status.bg, color: status.color, border: `1px solid ${status.border}`, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>{status.label}</div>}
          {e.faculty_comment && <div style={{ background: '#F0FFF6', border: '1px solid #A8DCC0', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#1E5C40' }}><strong>Faculty comment:</strong> "{e.faculty_comment}"</div>}
          {[
            { label: 'Date', val: e.date }, { label: 'MRN', val: e.mrn },
            { label: 'Setting', val: e.setting },
            { label: 'Supervisors', val: (e.assigned_faculty_names || []).join(', ') },
            { label: 'Supervision', val: e.supervision },
            { label: 'Milestone', val: `Level ${e.milestone_level} — ${level?.label}` },
          ].map((row, i) => row.val ? (
            <div key={i} style={{ background: '#fff', borderRadius: 8, padding: '12px 14px', marginBottom: 8, display: 'flex', gap: 12 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', minWidth: 90 }}>{row.label}</span>
              <span style={{ fontSize: 14, color: '#333', flex: 1 }}>{row.val}</span>
            </div>
          ) : null)}
          <div style={{ background: '#fff', borderRadius: 8, padding: '14px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 8 }}>Case Summary</div>
            <div style={{ fontSize: 14, color: '#333', lineHeight: 1.7 }}>{e.case_summary}</div>
          </div>
          {e.reflection && <div style={{ background: '#fff', borderRadius: 8, padding: '14px', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 8 }}>Reflection</div>
            <div style={{ fontSize: 14, color: '#333', lineHeight: 1.7 }}>{e.reflection}</div>
          </div>}
          <div style={{ background: '#fff', borderRadius: 8, padding: '14px', marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: 10 }}>Competencies</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(e.competency_ids || []).map(id => { const c = comp(id); return c ? <span key={id} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: c.color, color: '#fff' }}>{c.label}</span> : null; })}
            </div>
          </div>
          {e.approval_status === 'pending' && (
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { onEdit(e); }} style={{ flex: 1, padding: '13px', background: '#1A3A5C', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>✎ Edit</button>
              <button onClick={() => deleteEntry(e.id)} disabled={deleting} style={{ padding: '13px 18px', background: '#FFF0F0', color: '#C42A2A', border: '1px solid #F4A8A8', borderRadius: 10, fontSize: 14, cursor: 'pointer' }}>Delete</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#F7F4F0', minHeight: '100vh', paddingBottom: isMobile ? 80 : 0 }}>
      <div style={{ padding: isMobile ? '16px 16px 0' : '36px 40px 0', maxWidth: isMobile ? '100%' : 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 style={{ fontFamily: "'DM Serif Display'", fontSize: isMobile ? 22 : 30, color: '#1A1A1F' }}>My Log</h1>
          <span style={{ fontSize: 12, color: '#888' }}>{entries.length} cases · {activities.length} activities</span>
        </div>

        {/* Cases / Activities tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#E8E4E0', borderRadius: 10, padding: 3 }}>
          <button onClick={() => setTab('cases')}
            style={{ flex: 1, padding: '9px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'", background: tab === 'cases' ? '#fff' : 'transparent', color: tab === 'cases' ? '#1A3A5C' : '#888', boxShadow: tab === 'cases' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
            🏥 Cases ({entries.length})
          </button>
          <button onClick={() => setTab('activities')}
            style={{ flex: 1, padding: '9px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans'", background: tab === 'activities' ? '#fff' : 'transparent', color: tab === 'activities' ? '#1A3A5C' : '#888', boxShadow: tab === 'activities' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
            📋 Activities ({activities.length})
          </button>
        </div>

        {/* CASES TAB */}
        {tab === 'cases' && (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
              {['all','pending','approved','approved_with_comments','rejected'].map(f => {
                const labels = { all:'All', pending:'⏳ Pending', approved:'✓ Approved', approved_with_comments:'✓ With Comments', rejected:'✗ Rejected' };
                const colors = { all:'#555', pending:'#C4622A', approved:'#1E5C40', approved_with_comments:'#2D6A8A', rejected:'#8A2D2D' };
                const count  = f === 'all' ? entries.length : entries.filter(e => e.approval_status === f).length;
                return (
                  <button key={f} onClick={() => setFilter(f)}
                    style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${filter===f?colors[f]:'#DDD'}`, background: filter===f?colors[f]:'transparent', color: filter===f?'#fff':colors[f], fontSize: 12, cursor: 'pointer', fontFamily:"'DM Sans'", whiteSpace:'nowrap', fontWeight:500 }}>
                    {labels[f]} ({count})
                  </button>
                );
              })}
            </div>
            {filtered.length === 0
              ? <div style={{ textAlign:'center', padding:'60px 0', color:'#aaa', fontSize:15 }}>No entries found</div>
              : filtered.map(e => {
                  const status = APPROVAL_STATUSES[e.approval_status];
                  return (
                    <div key={e.id} onClick={() => setSelected(e)}
                      style={{ background:'#fff', borderRadius:10, padding: isMobile?'12px 14px':'14px 18px', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', cursor:'pointer', borderLeft:`3px solid ${status?.color||'#DDD'}`, marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4, flexWrap:'wrap' }}>
                            <span style={{ fontSize:12, color:'#999', fontWeight:600 }}>{e.date}</span>
                            <span style={{ fontSize:11, background:'#F0EDE8', color:'#666', padding:'2px 8px', borderRadius:20 }}>{e.setting}</span>
                            {e.is_difficult && <span style={{ fontSize:10, background:'#FFF0E8', color:'#C4622A', padding:'2px 8px', borderRadius:20, fontWeight:600 }}>⚑</span>}
                          </div>
                          <div style={{ fontSize:13, color:'#333', lineHeight:1.5 }}>{(e.case_summary||'').slice(0, isMobile?80:120)}{(e.case_summary||'').length>(isMobile?80:120)?'…':''}</div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, marginLeft:10 }}>
                          <span style={{ fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:20, background:status?.bg, color:status?.color, border:`1px solid ${status?.border}`, whiteSpace:'nowrap' }}>{status?.label}</span>
                          <span style={{ fontFamily:"'DM Serif Display'", fontSize:18, color:'#2D6A8A' }}>Lvl {e.milestone_level}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
          </>
        )}

        {/* ACTIVITIES TAB */}
        {tab === 'activities' && (
          <ActivityLog activities={activities} onRefresh={onRefreshActivities} isMobile={isMobile} />
        )}
      </div>
    </div>
  );
}
